import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cube } from "@phosphor-icons/react";

const RAMPS = {
  viridis: "linear-gradient(90deg,#440154,#3b528b,#21918c,#5ec962,#fde725)",
  prospect: "linear-gradient(90deg,#2a9d8f,#e9c46a,#e63946)",
  terrain: "linear-gradient(90deg,#2c7bb6,#abd9e9,#ffffbf,#fdae61,#d7191c)",
  gray: "linear-gradient(90deg,#1a1a1a,#e8e8e8)",
};
const fmtNum = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "-";
  const n = Number(v), a = Math.abs(n);
  return a >= 100 ? n.toFixed(0) : a >= 1 ? n.toFixed(1) : n.toFixed(3);
};

function Scene({ grid, elevMin, elevMax, texture, targets = [], lineaments = [], bbox, boundary }) {
  const mount = useRef(null);
  useEffect(() => {
    const el = mount.current;
    if (!el || !grid?.length) return;
    const W = el.clientWidth, H = el.clientHeight || 420;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b1220);
    const cam = new THREE.PerspectiveCamera(50, W / H, 0.1, 1000);
    cam.position.set(0, -120, 95);
    cam.up.set(0, 0, 1);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio);
    el.appendChild(renderer.domElement);

    const n = grid.length;
    const size = 140;
    const geo = new THREE.PlaneGeometry(size, size, n - 1, n - 1);
    const range = Math.max(1, elevMax - elevMin);
    const relief = 42;
    const pos = geo.attributes.position;
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        const idx = r * n + c;
        const h = ((grid[r][c] - elevMin) / range) * relief;
        pos.setZ(idx, h);
      }
    }
    geo.computeVertexNormals();

    let material;
    if (texture) {
      const tex = new THREE.TextureLoader().load(texture);
      tex.colorSpace = THREE.SRGBColorSpace;
      material = new THREE.MeshStandardMaterial({ map: tex, side: THREE.DoubleSide, roughness: 0.9 });
    } else {
      material = new THREE.MeshStandardMaterial({ color: 0x8a7a5c, side: THREE.DoubleSide });
    }
    const mesh = new THREE.Mesh(geo, material);
    scene.add(mesh);
    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const dir = new THREE.DirectionalLight(0xffffff, 1.1);
    dir.position.set(-80, -80, 120);
    scene.add(dir);

    // Drape ranked targets and lineaments onto the surface
    const overlays = [];
    if (bbox) {
      const [minx, miny, maxx, maxy] = bbox;
      const toMesh = (lon, lat, lift) => {
        const col = (lon - minx) / (maxx - minx) * (n - 1);
        const row = (maxy - lat) / (maxy - miny) * (n - 1);
        const ci = Math.min(n - 1, Math.max(0, Math.round(col)));
        const ri = Math.min(n - 1, Math.max(0, Math.round(row)));
        const z = ((grid[ri][ci] - elevMin) / range) * relief + (lift || 0);
        return new THREE.Vector3(-size / 2 + (col / (n - 1)) * size, size / 2 - (row / (n - 1)) * size, z);
      };
      // Bold, clearly-visible licence boundary ring draped on the surface
      if (boundary) {
        let ring = [];
        try {
          if (boundary.type === "Polygon") ring = boundary.coordinates[0];
          else if (boundary.type === "MultiPolygon") ring = boundary.coordinates[0][0];
        } catch (e) { ring = []; }
        if (ring.length > 1) {
          const pts = ring.map((c) => toMesh(c[0], c[1], 3.5));
          const curve = new THREE.CatmullRomCurve3(pts, true);
          const tube = new THREE.TubeGeometry(curve, Math.max(32, pts.length * 2), 0.9, 8, true);
          const tmat = new THREE.MeshStandardMaterial({ color: 0x38bdf8, emissive: 0x1e6fa8, emissiveIntensity: 0.7 });
          const tmesh = new THREE.Mesh(tube, tmat);
          scene.add(tmesh); overlays.push(tmesh, tube, tmat);
        }
      }
      const prioColor = (p) => p === "Priority 1" ? 0xe63946 : p === "Priority 2" ? 0xe9c46a : 0x2a9d8f;
      targets.forEach((t) => {
        if (!t.polygon) return;
        const pts = t.polygon.map((c) => toMesh(c[0], c[1], 2.5));
        const geo = new THREE.BufferGeometry().setFromPoints(pts);
        const line = new THREE.LineLoop(geo, new THREE.LineBasicMaterial({ color: prioColor(t.priority), linewidth: 2 }));
        scene.add(line); overlays.push(line, geo);
        if (t.centroid) {
          const c = toMesh(t.centroid.lon, t.centroid.lat, 5);
          const sg = new THREE.ConeGeometry(2, 6, 8);
          const sm = new THREE.MeshStandardMaterial({ color: prioColor(t.priority) });
          const cone = new THREE.Mesh(sg, sm); cone.position.copy(c); cone.rotation.x = Math.PI;
          scene.add(cone); overlays.push(cone, sg, sm);
        }
      });
      lineaments.filter((f) => f.properties?.status !== "rejected").slice(0, 150).forEach((f) => {
        const pts = f.geometry.coordinates.map((c) => toMesh(c[0], c[1], 1.2));
        const geo = new THREE.BufferGeometry().setFromPoints(pts);
        const col = (f.properties?.class || "").includes("Major") ? 0xff5a5a : 0xf59e0b;
        const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: col }));
        scene.add(line); overlays.push(line, geo);
      });
    }

    const controls = new OrbitControls(cam, renderer.domElement);
    controls.enableDamping = true;
    controls.target.set(0, 0, 15);

    let raf;
    const animate = () => { controls.update(); renderer.render(scene, cam); raf = requestAnimationFrame(animate); };
    animate();
    const onResize = () => { const w = el.clientWidth; renderer.setSize(w, H); cam.aspect = w / H; cam.updateProjectionMatrix(); };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf); window.removeEventListener("resize", onResize);
      controls.dispose(); geo.dispose(); material.dispose();
      overlays.forEach((o) => o.dispose && o.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, [grid, elevMin, elevMax, texture, targets, lineaments, bbox, boundary]);
  return <div ref={mount} className="w-full h-[420px] rounded-md overflow-hidden bg-[#0b1220]" data-testid="terrain-3d-canvas" />;
}

export default function Terrain3D({ project }) {
  const t = project.terrain;
  const [open, setOpen] = useState(false);
  const drapes = [];
  if (t) drapes.push(["hillshade", t.hillshade_png]);
  if (project.prospectivity) drapes.push(["prospectivity", project.prospectivity.png]);
  if (project.prospectivity_ml) drapes.push(["ml", project.prospectivity_ml.png]);
  if (t) { drapes.push(["elevation", t.elevation_png]); drapes.push(["slope", t.slope_png]); }
  if (project.geotiff_overlay?.png) drapes.push(["geotiff", project.geotiff_overlay.png]);
  const [drape, setDrape] = useState(drapes[0]?.[0] || "hillshade");
  const tex = drapes.find((d) => d[0] === drape)?.[1];

  const legend = (() => {
    if (drape === "geotiff") {
      const ov = project.geotiff_overlay || {};
      if (ov.single_band) return { title: "GeoTIFF value scale", grad: RAMPS.viridis, min: fmtNum(ov.value_min), max: fmtNum(ov.value_max) };
      return { title: "GeoTIFF overlay", rgb: true };
    }
    if (drape === "prospectivity") return { title: "Prospectivity", grad: RAMPS.prospect, min: "Low", max: "High" };
    if (drape === "ml") return { title: "ML prospectivity", grad: RAMPS.prospect, min: "Low", max: "High" };
    if (drape === "elevation") return { title: "Elevation (m)", grad: RAMPS.terrain, min: fmtNum(t.elev_min), max: fmtNum(t.elev_max) };
    if (drape === "slope") return { title: "Slope", grad: RAMPS.terrain, min: "Low", max: "High" };
    if (drape === "hillshade") return { title: "Hillshade", grad: RAMPS.gray, min: "Shadow", max: "Lit" };
    return null;
  })();

  if (!t) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" data-testid="terrain-3d-open"><Cube size={16} className="mr-1" weight="fill" />Open Interactive 3D Terrain</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle className="font-display">3D Terrain - {project.name}</DialogTitle>
          <DialogDescription>Interactive elevation model with draped analysis layers. Drag to orbit, scroll to zoom.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Drape layer:</span>
          <Select value={drape} onValueChange={setDrape}>
            <SelectTrigger className="h-8 w-full sm:w-56 text-xs" data-testid="terrain-3d-drape"><SelectValue /></SelectTrigger>
            <SelectContent>{drapes.map(([k]) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-[11px] text-muted-foreground ml-auto">Drag to orbit · scroll to zoom</span>
        </div>
        {open && (
          <div className="relative">
            <Scene grid={t.elevation_grid} elevMin={t.elev_min} elevMax={t.elev_max} texture={tex}
              targets={(project.canonical_targets?.length ? project.canonical_targets : project.targets) || []} lineaments={project.lineaments?.features || []}
              bbox={project.boundary_metrics?.bbox} boundary={project.boundary} />
            {legend && (
              <div className="absolute z-10 bottom-3 left-3 bg-white/90 backdrop-blur rounded-md shadow-md px-3 py-2 text-[10px] text-slate-700" data-testid="terrain-3d-legend">
                <div className="font-semibold mb-1 text-slate-800">{legend.title}</div>
                {legend.rgb ? (
                  <div className="flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: "linear-gradient(135deg,#c1440e,#2a6f97,#e9c46a)" }} /> True-colour composite (RGB)</div>
                ) : (
                  <>
                    <div className="h-2.5 w-40 rounded-sm" style={{ background: legend.grad }} role="img" aria-label={`${legend.title} colour scale`} />
                    <div className="flex justify-between w-40 mt-0.5 tabular-nums">
                      <span data-testid="terrain-3d-legend-min">{legend.min}</span>
                      <span data-testid="terrain-3d-legend-max">{legend.max}</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground font-mono">Elevation {t.elev_min}–{t.elev_max} m (Copernicus DEM GLO-30), vertical exaggeration applied. Cones mark ranked targets; lines are structural lineaments.</p>
      </DialogContent>
    </Dialog>
  );
}
